import React, { useState, useEffect } from 'react';
import ServiceReportApp from './ServiceReportApp';
import { LandingPage } from './components/LandingPage';
import { MachineConfigTranslatorPage } from './components/MachineConfigTranslator';
import { XOVRConverterPage } from './components/XOVRConverter';
import { TubeProPLCDebuggerPage } from './components/TubeProPLCDebugger';

/**
 * Top-level router for xovrtools.com
 *
 *   /                            -> Landing page (tool launcher)
 *   /servicereport               -> Service Report app (existing functionality)
 *   /Machine_Config_Translator   -> Storefront for the Windows translator EXE
 *   /XOVR_Converter              -> Storefront for the XOVR Converter Windows installer
 *   /TubePro_PLC_Debugger        -> Storefront for the TubePro PLC Debug Log Injector EXE
 *   /HPMprocessor                -> Redirect to https://hpm-processor.fly.dev
 *
 * Uses simple pathname matching; no router library needed. Supports
 * back/forward via popstate.
 */
const App: React.FC = () => {
    const [pathname, setPathname] = useState<string>(window.location.pathname);

    useEffect(() => {
        const onPopState = () => setPathname(window.location.pathname);
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // The Service Report editor needs a fixed-viewport, no-body-scroll
    // layout (it has its own internal scroll containers). Every other route
    // is a normal long page that should scroll with the body — especially
    // critical on mobile, where blocking body scroll hides the entire page
    // below the fold. Toggle the lock-down classes per route.
    useEffect(() => {
        const isEditorRoute =
            pathname === '/servicereport' || pathname.startsWith('/servicereport/');
        const body = document.body;
        const root = document.getElementById('root');
        if (isEditorRoute) {
            body.classList.add('h-screen', 'overflow-hidden');
            body.classList.remove('min-h-screen');
            root?.classList.add('h-full');
            root?.classList.remove('min-h-screen');
        } else {
            body.classList.remove('h-screen', 'overflow-hidden');
            body.classList.add('min-h-screen');
            root?.classList.remove('h-full');
            root?.classList.add('min-h-screen');
        }
    }, [pathname]);

    // /HPMprocessor -> external redirect
    if (pathname === '/HPMprocessor' || pathname.startsWith('/HPMprocessor/')) {
        window.location.replace('https://hpm-processor.fly.dev');
        return (
            <div style={{ padding: 40, color: '#888', fontFamily: 'system-ui', textAlign: 'center' }}>
                Redirecting to HPM Processor…
            </div>
        );
    }

    // /servicereport(/...) -> existing service report app
    if (pathname === '/servicereport' || pathname.startsWith('/servicereport/')) {
        return <ServiceReportApp />;
    }

    // /Machine_Config_Translator -> Windows translator EXE storefront
    if (pathname === '/Machine_Config_Translator' || pathname.startsWith('/Machine_Config_Translator/')) {
        return <MachineConfigTranslatorPage />;
    }

    // /XOVR_Converter -> XOVR Converter Windows installer storefront
    if (pathname === '/XOVR_Converter' || pathname.startsWith('/XOVR_Converter/')) {
        return <XOVRConverterPage />;
    }

    // /TubePro_PLC_Debugger -> TubePro PLC Debug Log Injector storefront
    if (pathname === '/TubePro_PLC_Debugger' || pathname.startsWith('/TubePro_PLC_Debugger/')) {
        return <TubeProPLCDebuggerPage />;
    }

    // / -> landing page
    if (pathname === '/' || pathname === '') {
        return <LandingPage />;
    }

    // Any other path: 404 -> landing
    return <LandingPage />;
};

export default App;
